import { FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, readInlineData } from 'react-relay/legacy';
import { Link } from 'dibs-elements/exports/Link';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import getBuyerSupportLink from 'dibs-support-link/exports/getBuyerSupportLink';

import { taxExemptHelpers_buyer$key } from './__generated__/taxExemptHelpers_buyer.graphql';

const buyerFragment = graphql`
    fragment taxExemptHelpers_buyer on User @inline {
        tradeFirm {
            taxDocumentation {
                stateDocuments {
                    state
                    expirationDate
                }
            }
        }
    }
`;

type TaxDocument = {
    state?: string | null;
    expirationDate?: string | null;
};

const isTaxDocumentValid = (doc: TaxDocument): boolean => {
    if (typeof doc.expirationDate === 'string') {
        return new Date() < new Date(doc.expirationDate);
    }
    // if document doesn't have expirationDate set, consider it valid
    return true;
};

export const getValidTaxDocuments = ({
    buyer,
}: {
    buyer: taxExemptHelpers_buyer$key;
}): TaxDocument[] => {
    const buyerData = readInlineData(buyerFragment, buyer);
    const stateDocuments = (buyerData?.tradeFirm?.taxDocumentation?.stateDocuments || []).filter(
        filterNulls
    );
    return stateDocuments.filter(isTaxDocumentValid);
};

export const getValidTaxDocumentForState = ({
    buyer,
    usState,
}: {
    buyer: taxExemptHelpers_buyer$key;
    usState: string;
}): TaxDocument | undefined => {
    const taxDocuments = getValidTaxDocuments({ buyer });
    return taxDocuments.find(doc => doc?.state === usState);
};

export const InvalidTaxDocumentText: FC<{ usState: string }> = ({ usState }) => (
    <FormattedMessage
        id="abc.taxExemptHelpers.hasInvalidTaxDocument"
        defaultMessage="If this purchase is eligible for tax exemption, please provide us with a valid resale or tax exemption certificate for {usState}."
        values={{ usState }}
    />
);

export const LearnMoreLink: FC<{ onClick: () => void }> = ({ onClick }) => (
    <>
        {' '}
        <Link
            href={getBuyerSupportLink('/en-us/articles/14491937576091/')}
            target="_blank"
            onClick={onClick}
        >
            <FormattedMessage id="abc.taxExemptHelpers.learnMore" defaultMessage="Learn more" />
        </Link>
    </>
);

export const EligibleForTaxExemptHeader: FC = () => (
    <FormattedMessage
        id="abc.taxExemptHelpers.eligibleForTaxExemptHeader"
        defaultMessage="1stDibs has a valid resale certificate on file."
    />
);
